import { ImageBlockModel } from '@blocksuite/affine/model';
import { FetchUtils } from '@blocksuite/affine/shared/adapters';
import type { BlockModel } from '@blocksuite/affine/store';
import type { GfxModel } from '@blocksuite/std/gfx';

export async function fetchImageToFile(
  url: string,
  filename: string,
  imageProxy?: string
): Promise<File | void> {
  try {
    const res = await FetchUtils.fetchImage(url, undefined, imageProxy);
    if (res && res.ok) {
      let blob = await res.blob();
      if (!blob.type || !blob.type.startsWith('image/')) {
        blob = await convertToPng(blob).then(tmp => tmp || blob);
      }
      return new File([blob], filename, { type: blob.type || 'image/png' });
    }
  } catch (err) {
    console.error(err);
  }

  return fetchImageFallback(url, filename);
}

function fetchImageFallback(
  url: string,
  filename: string
): Promise<File | void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext('2d');
      if (!ctx) {
        reject();
        return;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0);
      c.toBlob(blob => {
        if (blob) {
          return resolve(new File([blob], filename, { type: blob.type }));
        }
        resolve();
      }, 'image/png');
    };
    img.onerror = () => resolve();
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}

function convertToPng(blob: Blob): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', _ => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.width;
        c.height = img.height;
        const ctx = c.getContext('2d');
        if (!ctx) {
          reject();
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);
        c.toBlob(resolve, 'image/png');
      };
      img.onerror = () => resolve(null);
      img.src = reader.result as string;
    });
    reader.addEventListener('error', () => resolve(null));
    reader.readAsDataURL(blob);
  });
}

export function readBlobAsURL(
  blob: Blob | File,
  signal?: AbortSignal
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const reader = new FileReader();
    let settled = false;
    const cleanup = () => {
      signal?.removeEventListener('abort', abort);
      reader.onload = null;
      reader.onerror = null;
      reader.onabort = null;
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const abort = () => {
      reader.abort();
      fail(signal?.reason);
    };
    reader.onload = () => {
      if (settled) return;
      if (typeof reader.result !== 'string') {
        fail(new TypeError('Invalid image preview result'));
        return;
      }
      settled = true;
      cleanup();
      resolve(reader.result);
    };
    reader.onerror = () =>
      fail(reader.error ?? new Error('Failed to read image preview'));
    reader.onabort = () =>
      fail(
        signal?.reason ??
          new DOMException('Image preview aborted', 'AbortError')
      );
    signal?.addEventListener('abort', abort, { once: true });
    try {
      reader.readAsDataURL(blob);
    } catch (error) {
      fail(error);
    }
  });
}

/** Native data-URL decoding avoids a full binary string and per-byte JS copy. */
export async function dataUrlToFile(
  dataUrl: string,
  fileName: string,
  type: string,
  signal?: AbortSignal
): Promise<File> {
  signal?.throwIfAborted();
  if (!dataUrl.startsWith('data:'))
    throw new TypeError('Expected an image data URL');
  const response = await fetch(dataUrl, { signal });
  signal?.throwIfAborted();
  const blob = await response.blob();
  signal?.throwIfAborted();
  return new File([blob], fileName, { type });
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  type = 'image/png',
  quality?: number
) {
  return new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, type, quality)
  );
}

export function randomSeed(min = 0, max = Date.now()) {
  return Math.round(Math.random() * (max - min)) + min;
}

export function isImage(
  model: GfxModel | BlockModel
): model is ImageBlockModel {
  return model instanceof ImageBlockModel;
}
